import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { api } from '@next-nx/utils'
import { useSite, useUtente } from '@next-nx/hooks'
import { TOOLBAR } from '@next-nx/utils/endpoints'
import { Button, Tooltip } from '@next-nx/shared-ui/atoms'
import { Info } from '@next-nx/shared-ui/icons'
import styles from './Toolbar.module.sass'
import { LandingType, PaginaType } from '@next-nx/utils/types'
import { UNIPOLRENTAL } from '@next-nx/utils/settings'
import { useSelector } from 'react-redux'

api.defaults.xsrfCookieName = 'csrftoken'
api.defaults.xsrfHeaderName = 'X-CSRFTOKEN'

interface Props {
  page?: PaginaType
  item?: any
}

const Toolbar = (props: Props) => {
  const { page = null, item = null } = props

  const { landing }: { landing: LandingType } = useSelector((state: any) => state?.landing)
  const router = useRouter()
  const locale = router.locale || landing?.siteId.toString()
  const site = useSite(locale)
  const { utente } = useUtente()

  const previewModeAbilitata = site === UNIPOLRENTAL
  const [paginaContienePagebuilder, setPaginaContienePagebuilder] = useState(false)

  useEffect(() => {
    setTimeout(() => {
      setPaginaContienePagebuilder(document.getElementsByClassName('pb-content').length > 0)
    }, 1000)
  }, [router.asPath])

  if (!utente) return null

  if (!(utente.isStaff || utente.isImpersonate)) return null

  const handleSwitchMode = (edit: boolean) => {
    api
      .post(TOOLBAR + 'set_edit/', { edit })
      .then(() => {
        router.reload()
      })
      .catch((error) => console.log(error))
  }

  const adminUrl = (page.adminUrl ? new URL(page.adminUrl)?.origin || '' : '') + '/d/admin/'

  const stopImpersonate = () => {
    api.post(utente.stopImpersonateUrl).then((res) => {
      window.location.replace(res.request.responseURL)
    })
  }

  const VisibilitaTooltipDescription = (item: any) => {
    return (
      <div>
        <p>Stato pubblicazione: {item.pubblicato ? 'pubblicato' : 'non publicato'}</p>
        {item.visibilitaDal && <p>Visibilità dal: {item.visibilitaDal}</p>}
        {item.visibilitaAl && <p>Visibilità fino al: {item.visibilitaAl}</p>}
      </div>
    )
  }

  return (
    <div className={`${styles.toolbar} toolbar`}>
      {utente.isImpersonate ? (
        <div className={`${styles.user} ${styles['user--impersonate']}`}>
          Stai impersonando{' '}
          {utente.nome || utente.cognome ? (
            <span>
              {utente.nome} {utente.cognome}
            </span>
          ) : (
            <span>{utente.email}</span>
          )}{' '}
          - <a href="#" onClick={stopImpersonate}>Smetti di impersonare</a>
        </div>
      ) : (
        <>
          <Button
            label="Admin"
            size="sm"
            href={adminUrl}
            external
            newPage
            className={`${styles.adminLink} ${styles.button}`}
          />
          <div className={styles.user}>
            {utente.nome} {utente.cognome}
          </div>
          <section className={styles.left} />
          <section className={styles.right}>
            {previewModeAbilitata &&
              (item && 'visibile' in item ? (
                <span>
                  Stato:{' '}
                  <span className={`${styles[`visibilita--${item.visibile}`]}`}>
                    {item.visibile ? 'visibile' : 'non visibile'}
                  </span>
                  <Tooltip
                    id={`tooltip-toolbar-item-visibilita`}
                    label={<VisibilitaTooltipDescription item={item} />}
                    pos="bottom"
                    className={styles.tooltip}
                  >
                    <span className={styles.info}>
                      <Info />
                    </span>
                  </Tooltip>
                </span>
              ) : (
                page &&
                'visibile' in page && (
                  <span>
                    Stato:{' '}
                    <span className={`${styles[`visibilita--${page.visibile}`]}`}>
                      {page.visibile ? 'visibile' : 'non visibile'}
                    </span>
                    <Tooltip
                      id={`tooltip-toolbar-page-visibilita`}
                      label={<VisibilitaTooltipDescription item={page} />}
                      pos="bottom"
                      className={styles.tooltip}
                    >
                      <span className={styles.info}>
                        <Info />
                      </span>
                    </Tooltip>
                  </span>
                )
              ))}
            {item && item.adminUrl && (
              <Button
                label={`Modifica ${item.modelClass}`}
                size="sm"
                variant="ghost"
                href={item.adminUrl}
                external
                newPage
              />
            )}
            {page && page.adminUrl && (
              <Button
                label="Admin Pagina"
                size="sm"
                variant="ghost"
                href={page.adminUrl}
                external
                newPage
              />
            )}
            {paginaContienePagebuilder &&
              (utente.edit ? (
                <Tooltip
                  id={`tooltip-toolbar-vedi-pubblicata`}
                  label={'Visualizza il contenuto pubblicato della pagina'}
                  pos="bottom"
                  className={styles.tooltip}
                >
                  <div className={styles.button}>
                    <Button
                      label="Vedi pubblicata"
                      size="sm"
                      onClick={() => handleSwitchMode(false)}
                      className={styles.button}
                    />
                  </div>
                </Tooltip>
              ) : (
                <Tooltip
                  id={`tooltip-toolbar-modifica`}
                  label={'Modifica il contenuto della pagina col pagebuilder'}
                  pos="bottom"
                  className={styles.tooltip}
                >
                  <div className={styles.button}>
                    <Button
                      label="Modifica"
                      size="sm"
                      onClick={() => handleSwitchMode(true)}
                      className={styles.button}
                    />
                  </div>
                </Tooltip>
              ))}
            {previewModeAbilitata &&
              (router.isPreview ? (
                <Tooltip
                  id={`tooltip-toolbar-stop-preview`}
                  label={
                    'Con la modalità preview disattivata non potrai visualizzare le pagine nello stato "non visibile"'
                  }
                  pos="bottom"
                  className={styles.tooltip}
                >
                  <div className={styles.button}>
                    <Button
                      external
                      label="Disattiva modalità preview"
                      size="sm"
                      href={`/api/stop-preview?secret=${process.env.PREVIEW_MODE_SECRET}&redirect=${router.asPath}`}
                      className={styles.button}
                    />
                  </div>
                </Tooltip>
              ) : (
                <Tooltip
                  id={`tooltip-toolbar-attiva-preview`}
                  label={
                    'Con la modalità preview attiva puoi visualizzare le pagine anche se nello stato "non visibile"'
                  }
                  pos="bottom"
                  className={styles.tooltip}
                >
                  <div className={styles.button}>
                    <Button
                      external
                      label="Attiva modalità preview"
                      size="sm"
                      href={`/api/preview?secret=${process.env.PREVIEW_MODE_SECRET}&redirect=${router.asPath}`}
                      className={styles.button}
                    />
                  </div>
                </Tooltip>
              ))}
          </section>
        </>
      )}
    </div>
  )
}

export default Toolbar
